import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { _MatCheckboxRequiredValidatorModule, MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BackendService } from 'src/app/core/backend.service';
import { CustomLabelsService } from 'src/app/core/custom-labels.service';
import { selectCatalogSettings } from 'src/app/root-store/catalog-store/selectors';
import { CatalogSettings } from 'src/app/root-store/catalog-store/state';
import { RootState } from 'src/app/root-store/root-state.model';
import * as UserActions from 'src/app/root-store/user-store/actions';
import { selectUserProfile } from 'src/app/root-store/user-store/selectors';
import { SubSink } from 'subsink';
import { UserResult } from '../user.interfaces';

export enum UserProfileFormMode {
  Registration = 'registration',
  EditProfile = 'edit-profile'
}

export interface UserProfileDialogData {
  formMode: UserProfileFormMode
  email: string
}

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  standalone: true,
  imports: [
    CommonModule, MatIconModule, FormsModule, ReactiveFormsModule, _MatCheckboxRequiredValidatorModule, TranslateModule,
    MatDialogModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatInputModule,
    MatCheckboxModule
  ]
})
export class UserProfileComponent implements OnInit, OnDestroy {

  profileForm = this.fb.group({
    greeting: [''],
    company: [''],
    firstName: [''],
    lastName: [''],
    street: [''],
    zip: [''],
    city: [''],
    email: [''],
    phone: [''],
    password: [''],
    privacyAccepted: [false]
  })
  isSubmitting = false
  formMode!: UserProfileFormMode

  private subs = new SubSink()
  settings!: CatalogSettings

  constructor(
    private fb: FormBuilder,
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<UserProfileComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private data: UserProfileDialogData,
    private backend: BackendService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    public customLabels: CustomLabelsService,
  ) { }

  ngOnInit() {
    this.settings = this.store.selectSignal(selectCatalogSettings)()

    this.formMode = this.data.formMode

    if (this.data.email)
      this.profileForm.controls.email.setValue(this.data.email)

    if (this.formMode === UserProfileFormMode.EditProfile) {
      const profile = this.store.selectSignal(selectUserProfile)()
      this.profileForm.patchValue(profile!)
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }

  async openLogin(): Promise<void> {
    this.dialogRef.close()

    this.dialog.open(
      (await import('../login/login.component')).LoginComponent,
      {
        data: { email: this.profileForm.controls.email.value }
      })
  }

  onSubmit() {
    this.isSubmitting = true

    let result$: Observable<UserResult>
    let successMsgKey: string

    switch (this.formMode) {
      case UserProfileFormMode.Registration:
        result$ = this.backend.registerUser(this.profileForm.value)
        successMsgKey = _('PROFILE.REGISTRATION_SUCCESS')
        break
      case UserProfileFormMode.EditProfile:
        result$ = this.backend.updateMyProfile(this.profileForm.value)
        successMsgKey = _('PROFILE.UPDATE_SUCCESS')
        break
    }

    result$
      .pipe(tap(() => this.isSubmitting = false))
      .subscribe({
        next: res => {
          if (res.identityResult!.succeeded) {
            this.store.dispatch(UserActions.LoadUser())
            this.dialogRef.close()

            this.snackBar.open(this.translate.instant(successMsgKey), undefined, { duration: 5000 })
          } else
            this.profileForm.setErrors({ serverError: res.identityResult!.errors.map(e => e.description || e.code).join(' ') })
        }, error: error => {
          this.profileForm.setErrors({ serverError: error })
        }
      })
  }
}
